import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import {
  UserGroup,
  UserGroupPlatform,
  UserGroupPlatformService,
  UserGroupService,
  UserGroupType,
  UserGroupTypeService,
} from 'us-web-services';
import useUser from '../../store/useUser';
import DisplayService from '../../util/DisplayService';
import CardStyles from '../../styles/CardStyles';

function UserGroupEdit(props) {
  const { userId } = props;
  const { classes } = CardStyles();
  const userState = useUser()[0];
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [groupTypes, setGroupTypes] = useState<UserGroupType[]>([]);
  const [platforms, setPlatforms] = useState<UserGroupPlatform[]>([]);

  const showError = error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an error updating the user group.',
    );

    toast.error(displayedError.message);
  };

  const getGroups = async (): Promise<void> => {
    const config = {
      params: {
        userId,
      },
    };
    const response = await UserGroupService.getByFilter(config);

    setGroups(response.data.data);
  };

  const getGroupTypes = async (): Promise<void> => {
    const config = {
      params: {
        limit: 100,
        sort: 'created',
        'created.dir': 'desc',
      },
    };
    const response = await UserGroupTypeService.getByFilter(config);

    setGroupTypes(response.data.data);
  };

  const getPlatforms = async (): Promise<void> => {
    const config = {
      params: {
        enabled: true,
        limit: 100,
      },
    };
    const response = await UserGroupPlatformService.getByFilter(config);

    setPlatforms(response.data.data);
  };

  const removeUserGroup = async (groupId: number): Promise<void> => {
    try {
      await UserGroupService.delete(groupId);
      await getGroups();
      toast.success('Group removed');
    } catch (e) {
      showError(e);
    }
  };

  const addUserGroup = async (groupType: UserGroupType): Promise<void> => {
    if (!groupType) return;

    try {
      const data = {
        group: {
          id: groupType.id,
        },
        userId,
      };

      await UserGroupService.create(data);
      await getGroups();
      toast.success('Group assigned');
    } catch (e) {
      showError(e);
    }
  };

  useEffect(() => {
    getGroups();
    getGroupTypes();
    getPlatforms();
  }, [
    userId,
    userState.user.id,
    userState.user.parent,
    userState.user.sitter,
  ]);

  const enabledCampaignIds = platforms
    .filter(p => p.enabled)
    .map(p => p.campaignId);

  const availableGroupTypes = groupTypes
    .filter(gt => enabledCampaignIds.includes(gt.campaignId))
    .filter(gt => !groups.some(g => g.group?.id === gt.id));

  return (
    <Card className={classes.sectionCard}>
      <CardContent className={classes.sectionContent}>
        <Typography className={classes.sectionHeader}>
          User Groups
        </Typography>

        {groups.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {groups.map(group => (
              <Chip
                key={group.id}
                label={`${group?.group?.name} - ${group?.group?.label}`}
                onDelete={() => removeUserGroup(group.id)}
                color='primary'
                variant='outlined'
              />
            ))}
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            No groups assigned.
          </Typography>
        )}

        {availableGroupTypes.length > 0 && (
          <Autocomplete
            options={availableGroupTypes}
            getOptionLabel={(option) => `${option.name} - ${option.label}`}
            onChange={(_, value) => {
              if (value) addUserGroup(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Add group'
                placeholder='Search available groups...'
                size='small'
              />
            )}
            size='small'
            sx={{ maxWidth: 400 }}
            value={null}
            blurOnSelect
            clearOnBlur
          />
        )}
      </CardContent>
    </Card>
  );
}

export default UserGroupEdit;
